import { parseRoute } from 'src/wac-routes';

/**
 * Matches a resolved route against a whitelist pattern.
 *
 * Patterns are matched exactly, except when they end in `/*`, which matches the
 * prefix segment and any descendant (e.g. `/software/jira/*` matches
 * `/software/jira` and `/software/jira/agile`).
 *
 * This is the same matching used by the WAC route-list dynamic configs in
 * `resolve-page-props.ts` (user-context, sales-leads CTA, etc.). Keep the two
 * in sync — both consume this helper.
 */
export const matchesRoutePattern = (route: string, pattern: string): boolean => {
	if (pattern.endsWith('/*')) {
		const prefix = pattern.slice(0, -2);
		return route === prefix || route.startsWith(`${prefix}/`);
	}
	return route === pattern;
};

/**
 * Returns the current page's locale-stripped, leading-slash-prefixed route
 * (e.g. `/fr/software/jira` → `/software/jira`, home → `/`).
 *
 * This is the canonical page-path key used to compare against WAC route-list
 * dynamic configs. It reuses `parseRoute` — the same route parser that
 * `resolve-page-props.ts` uses server-side — so client-side allowlist checks
 * key off exactly the same path the rest of the routing layer sees.
 *
 * Pass an explicit `location` to override; when omitted, the current
 * `globalThis.location` is used. SSR-safe: returns `''` when there is no
 * `location` available (no window).
 */
export const getNonLocalizedRoute = (location?: Location): string => {
	const resolvedLocation =
		location ?? (typeof globalThis !== 'undefined' ? globalThis.location : undefined);
	if (!resolvedLocation) {
		return '';
	}
	return `/${parseRoute(resolvedLocation).path}`;
};

/**
 * True when the current route matches any pattern in `patterns` (exact or
 * `/*` glob), comparing against the locale-stripped page path. An empty
 * `patterns` list matches nothing.
 */
export const isRouteInPatternList = (patterns: string[], location?: Location): boolean => {
	const route = getNonLocalizedRoute(location);
	if (!route) {
		return false;
	}
	return patterns.some((pattern) => matchesRoutePattern(route, pattern));
};
